import type { GuideLang } from "@/lib/virtualspace/guide/guide-public-shape";

/** Distinctions are ankhs (☥), the hieroglyph for life: 0 Selected … 3 Legendary. */
export const GUIDE_DISTINCTION_NAMES: Record<GuideLang, readonly string[]> = {
  en: ["Selected", "Remarkable", "Outstanding", "Legendary"],
  ar: ["مختار", "لافت", "متميّز", "أسطوري"],
};

export const GUIDE_DISTINCTION_NOTES: Record<GuideLang, readonly string[]> = {
  en: [
    "Visited, and worth your time. Every place in Localized starts here.",
    "A standout in its city. Plan an evening around it.",
    "Among the best in Egypt. Worth a journey across the country.",
    "A reason to come to Egypt. Awarded rarely, reviewed every year.",
  ],
  ar: [
    "تمت زيارته ويستحق وقتك. كل مكان في Localized يبدأ من هنا.",
    "علامة فارقة في مدينته. خطّط لأمسيتك حوله.",
    "من أفضل ما في مصر. يستحق السفر عبر البلاد.",
    "سبب لزيارة مصر. يُمنح نادراً ويُراجع كل عام.",
  ],
};

/**
 * Copy with a blank: "Results for “{q}”" filled from `vars`. Every line of
 * copy is plain text (never a function), because the copy objects cross
 * into browser components whole, and Next refuses to send a function there.
 */
export function fillCopy(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => (key in vars ? String(vars[key]) : match));
}

/** "{n} place" or "{n} places", filled. */
export function countCopy(one: string, many: string, n: number): string {
  return fillCopy(n === 1 ? one : many, { n, count: n });
}

export const GUIDE_PUBLIC_COPY = {
  en: {
    brand: "Localized",
    brandPlace: "Egypt",
    brandFull: "Localized Egypt",
    tagline: "Know it like a local.",
    by: "by VirtualSpace",
    edition: "The 2026 selection · Launch edition",
    headline: "Know Egypt like a local.",
    headlineTwin: "اعرف مصر كأهلها",
    headlineTwinLang: "ar",
    sub: "Hand-picked places, each one visited by our editors and scanned in 3D, so you can step inside before you go.",
    explore: "Explore the map",
    nav: { selection: "The Selection", map: "Map", events: "Events", journal: "Journal" },
    numerals: ["I", "II", "III", "IV", "V"],
    stats: {
      places: "places in the selection",
      regions: "regions so far",
      tours: "walk-through 3D tours by VirtualSpace",
      events: "events on the calendar",
    },
    selectionEyebrow: "Launch edition",
    selection: "The Selection",
    selectionTwin: "المختارات",
    mapEyebrow: "The map",
    mapTitle: "Follow the Nile",
    mapTwin: "على ضفاف النيل",
    mapSub: "Choose a region to zoom into its real streets: every place, what's on and the news there, pinned on one map.",
    comingNext: "Coming next",
    luxorAswan: "Luxor & Aswan",
    eventsEyebrow: "What's on in Egypt",
    eventsTitle: "What's on",
    eventsTwin: "الفعاليات",
    eventsNote:
      "Dates are checked against the organisers and the press. \"To confirm\" means a date or venue was reported in one place only.",
    onNow: "On now",
    officialSite: "Details",
    noEvents: "Events will appear here.",
    allEvents: "Everything on",
    elsewhere: "Across Egypt",
    whatsOnIntro:
      "Festivals, concerts, exhibitions and the seasons that shape Egypt's calendar, from now until next summer. Choose a region to narrow it down.",
    journalEyebrow: "News",
    journalTitle: "The Journal",
    journalTwin: "اليوميات",
    inTheSelection: "In the Selection",
    source: "Source",
    readStory: "Read at the source",
    noNews: "News from Localized Egypt will appear here.",
    allNews: "The whole Journal",
    journalIntro:
      "What's new and talked about in Egypt: openings, awards, discoveries and travel news, summarised by our editors. Every story links to its source.",
    distinctionsEyebrow: "Our distinctions",
    distinctionTitle: "The ankh is our star",
    all: "All",
    allRegions: "All of Egypt",
    region: "Region",
    category: "Category",
    places: "places",
    place: "place",
    layerAll: "Everything",
    layerPlaces: "Places",
    layerEvents: "What's on",
    layerNews: "News",
    events: "events",
    event: "event",
    stories: "stories",
    story: "story",
    acrossEgypt: "Across Egypt",
    acrossEgyptNote: "Nationwide, with no single place on the map.",
    emptyEvents: "Nothing on here yet. Try another region.",
    emptyNews: "No stories here yet. Try another region.",
    empty: "No selections here yet. Try another region or category.",
    mapUnavailable: "The map isn't available on this device. Here is the full list.",
    nearestFirst: "Nearest first",
    recentre: "Show everything on the map",
    legendCluster: "a group: click to zoom in",
    legendHere: "you, when you ask the map where you are",
    legendHint: "Map key",
    clusterOf: "on the map here, click to zoom in",
    youAreHere: "You are here",
    alsoNearby: "Also in the Selection",
    openNow: "Open now",
    closedNow: "Closed now",
    filterOpenNow: "Open now",
    eventWindows: { all: "All dates", now: "On now", week: "This week", month: "This month" },
    styleStreets: "Streets",
    styleSatellite: "Satellite",
    shareView: "Share this view",
    linkCopied: "Link copied",
    loadingMap: "Loading the map…",
    lightbox: { open: "View larger", close: "Close", prev: "Previous photo", next: "Next photo", of: "of" },
    share: { title: "Share", whatsapp: "WhatsApp", copy: "Copy link", sheet: "Share…" },
    tour: "Step inside in 3D",
    tourShort: "3D tour",
    tourWalk: "Walk through in 3D",
    tourClose: "Close the tour",
    viewVenue: "View venue",
    book: "Book a table",
    website: "Website",
    menu: "Menu",
    whatsapp: "WhatsApp",
    directions: "Directions",
    address: "Address",
    hours: "Opening hours",
    hoursUnknown: "Check opening hours with the venue before you go.",
    closed: "Closed",
    onTheMap: "On the map",
    distinction: "Distinction",
    editorsNote: "The editors' note",
    highlights: "Highlights",
    whatToOrder: "What to order",
    goodToKnow: "Good to know",
    awardsPress: "Awards & press",
    cuisine: "Cuisine",
    price: "Price",
    priceBands: ["Under EGP 500 a person", "EGP 500–1,500 a person", "EGP 1,500–3,500 a person", "Over EGP 3,500 a person"],
    call: "Call",
    instagram: "Instagram",
    facebook: "Facebook",
    fromTheScan: "From the scan",
    alsoIn: "Also in",
    back: "The Selection",
    tourCaption: "Captured by VirtualSpace. Drag to look around, tap the floor to walk. Works on phones and headsets.",
    language: "العربية",
    languageLabel: "اقرأ بالعربية",
    footer: "Hand-picked by editors who visit every place. Made by VirtualSpace, who scanned every room in 3D.",
    footerTwin: "اعرف مصر كأهلها",
    days: { mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday" },
    groups: { see: "See", do: "Do", eat: "Eat, drink & sleep" },
    browseEyebrow: "By category",
    browseTitle: "What are you after?",
    browseTwin: "ماذا تبحث عن؟",
    search: {
      label: "Search",
      placeholder: "Search places, events and stories",
      title: "Search",
      twin: "بحث",
      resultsFor: "Results for “{q}”",
      none: "Nothing found for “{q}”.",
      hint: "Try another word, a district, or a kind of place.",
      venues: "Places",
      events: "What's on",
      stories: "Stories",
      go: "Search",
      suggestions: "Suggestions",
    },
    saved: {
      save: "Save",
      saved: "Saved",
      unsave: "Remove from saved",
      title: "Saved places",
      twin: "أماكني المحفوظة",
      link: "Saved",
      empty: "Nothing saved yet. Tap Save on a place to keep it here.",
      note: "Saved on this device only. Share a list to keep it anywhere.",
      countOne: "{n} place",
      countMany: "{n} places",
      listName: "Name this list",
      listPlaceholder: "Cairo weekend",
      share: "Share this list",
      copyLink: "Copy link",
      copied: "Link copied",
      sharedTitle: "A list shared with you",
      sharedBy: "Shared from Localized Egypt",
      saveAll: "Save all to my places",
      clear: "Clear my saved places",
      confirmClear: "Remove every saved place?",
      missing: "Some places on this list are no longer in the Selection.",
      map: "On the map",
    },
    newsletter: {
      title: "What's on, every Thursday",
      twin: "كل خميس",
      body: "One email a week: what's on across Egypt, new places in the Selection, and the stories worth your time. Free, in English or Arabic.",
      email: "Your email",
      regions: "Regions you care about",
      allRegions: "All of Egypt",
      submit: "Subscribe",
      submitting: "Sending…",
      pending: "Check your inbox and confirm, and you're in.",
      confirmed: "You're in. See you Thursday.",
      confirmedBody: "The first What's on arrives this Thursday morning. You can unsubscribe from any email.",
      unsubscribed: "You've been unsubscribed.",
      unsubscribedBody: "No more emails from Localized Egypt. You can subscribe again any time.",
      invalid: "That link isn't valid or has already been used.",
      error: "That didn't work. Check the address and try again.",
      tooMany: "Too many tries. Please wait a while.",
      whatsapp: "Follow on WhatsApp",
      whatsappBody: "The week's picks in a WhatsApp channel.",
      rss: "RSS feed",
      privacy: "Your address is used for this email only.",
    },
    filters: {
      price: "Price",
      distinction: "Distinction",
      tour: "3D tour",
      sort: "Sort",
      sortDefault: "Editors' order",
      sortName: "A to Z",
      sortDistinction: "Distinction first",
      sortNearest: "Nearest first",
      clear: "Clear filters",
      showMap: "Map",
      showList: "List",
      any: "Any",
      more: "More filters",
    },
    eventPage: {
      addToCalendar: "Add to calendar",
      google: "Google Calendar",
      ics: "Apple, Outlook (.ics)",
      when: "When",
      where: "Where",
      nextDates: "Next dates",
      weekly: "Every week",
      monthly: "Every month",
      until: "until {date}",
      tickets: "Tickets & details",
      past: "This event has passed. Here is what's on next.",
      more: "More of what's on",
      back: "What's on",
    },
    collection: {
      placesIn: "Places in {name}",
      category: "{name} in Egypt",
      eventsIn: "What's on in {name}",
      storiesIn: "Stories from {name}",
      areas: "Areas",
      categories: "Categories",
      regions: "Regions",
      onTheMap: "On the map",
      introOne: "{count} place, visited by our editors.",
      introMany: "{count} places, each visited by our editors.",
      empty: "Nothing here yet. The editors are on it.",
      seeAll: "All of the Selection",
    },
    reviews: {
      bestFor: "Best for",
      title: "What reviewers say",
      rating: "{rating} on Google · {count} reviews",
    },
    atThisVenue: "What's on here",
    breadcrumbHome: "Localized Egypt",
    errors: {
      notFound: "Not on the map",
      notFoundBody: "We couldn't find that page. It may have moved, or never existed.",
      error: "Something went wrong",
      errorBody: "The page couldn't load. Try again in a moment.",
      retry: "Try again",
      home: "Back to Localized Egypt",
      loading: "Loading…",
    },
  },
  ar: {
    brand: "Localized",
    brandPlace: "مصر",
    brandFull: "Localized مصر",
    tagline: "اعرفها كأهلها.",
    by: "من VirtualSpace",
    edition: "مختارات 2026 · إصدار الإطلاق",
    headline: "اعرف مصر كأهلها",
    headlineTwin: "Know Egypt like a local.",
    headlineTwinLang: "en",
    sub: "أماكن مختارة بعناية، زارها محرّرونا وصوّرناها بتقنية ثلاثية الأبعاد، لتدخلها قبل أن تذهب.",
    explore: "استكشف الخريطة",
    nav: { selection: "المختارات", map: "الخريطة", events: "الفعاليات", journal: "اليوميات" },
    numerals: ["١", "٢", "٣", "٤", "٥"],
    stats: {
      places: "أماكن في المختارات",
      regions: "مناطق حتى الآن",
      tours: "جولات ثلاثية الأبعاد من VirtualSpace",
      events: "فعاليات في التقويم",
    },
    selectionEyebrow: "إصدار الإطلاق",
    selection: "المختارات",
    selectionTwin: "The Selection",
    mapEyebrow: "الخريطة",
    mapTitle: "على ضفاف النيل",
    mapTwin: "Follow the Nile",
    mapSub: "اختر منطقة لتقترب من شوارعها الحقيقية: كل مكان وكل فعالية وكل خبر فيها، على خريطة واحدة.",
    comingNext: "قريباً",
    luxorAswan: "الأقصر وأسوان",
    eventsEyebrow: "ماذا يحدث في مصر",
    eventsTitle: "الفعاليات",
    eventsTwin: "What's on",
    eventsNote: "نتحقّق من المواعيد لدى المنظمين والصحافة. «قيد التأكيد» يعني أن الموعد أو المكان ورد في مصدر واحد فقط.",
    onNow: "الآن",
    officialSite: "التفاصيل",
    noEvents: "ستظهر الفعاليات هنا.",
    allEvents: "كل الفعاليات",
    elsewhere: "في أنحاء مصر",
    whatsOnIntro: "المهرجانات والحفلات والمعارض والمواسم التي ترسم تقويم مصر، من الآن حتى الصيف المقبل. اختر منطقة لتضييق القائمة.",
    journalEyebrow: "أخبار",
    journalTitle: "اليوميات",
    journalTwin: "The Journal",
    inTheSelection: "من المختارات",
    source: "المصدر",
    readStory: "اقرأ في المصدر",
    noNews: "ستظهر أخبار Localized مصر هنا.",
    allNews: "كل اليوميات",
    journalIntro: "الجديد وحديث الناس في مصر: افتتاحات وجوائز واكتشافات وأخبار السفر، يلخّصها محرّرونا. كل خبر مرتبط بمصدره.",
    distinctionsEyebrow: "تميّزاتنا",
    distinctionTitle: "مفتاح الحياة هو نجمتنا",
    all: "الكل",
    allRegions: "كل مصر",
    region: "المنطقة",
    category: "الفئة",
    places: "أماكن",
    place: "مكان",
    layerAll: "الكل",
    layerPlaces: "الأماكن",
    layerEvents: "الفعاليات",
    layerNews: "الأخبار",
    events: "فعاليات",
    event: "فعالية",
    stories: "أخبار",
    story: "خبر",
    acrossEgypt: "في كل مصر",
    acrossEgyptNote: "على مستوى البلاد، دون مكان واحد على الخريطة.",
    emptyEvents: "لا فعاليات هنا بعد. جرّب منطقة أخرى.",
    emptyNews: "لا أخبار هنا بعد. جرّب منطقة أخرى.",
    empty: "لا توجد مختارات هنا بعد. جرّب منطقة أو فئة أخرى.",
    mapUnavailable: "الخريطة غير متاحة على هذا الجهاز. إليك القائمة الكاملة.",
    nearestFirst: "الأقرب أولاً",
    recentre: "أظهر كل شيء على الخريطة",
    legendCluster: "مجموعة: اضغط للتقريب",
    legendHere: "أنت، حين تسأل الخريطة عن مكانك",
    legendHint: "مفتاح الخريطة",
    clusterOf: "على الخريطة هنا، اضغط للتقريب",
    youAreHere: "أنت هنا",
    alsoNearby: "أيضاً في المختارات",
    openNow: "مفتوح الآن",
    closedNow: "مغلق الآن",
    filterOpenNow: "مفتوح الآن",
    eventWindows: { all: "كل المواعيد", now: "الآن", week: "هذا الأسبوع", month: "هذا الشهر" },
    styleStreets: "الشوارع",
    styleSatellite: "القمر الصناعي",
    shareView: "شارك هذا العرض",
    linkCopied: "تم نسخ الرابط",
    loadingMap: "جارٍ تحميل الخريطة…",
    lightbox: { open: "عرض أكبر", close: "إغلاق", prev: "الصورة السابقة", next: "الصورة التالية", of: "من" },
    share: { title: "مشاركة", whatsapp: "واتساب", copy: "نسخ الرابط", sheet: "مشاركة…" },
    tour: "ادخل المكان ثلاثي الأبعاد",
    tourShort: "جولة ثلاثية الأبعاد",
    tourWalk: "تجوّل ثلاثي الأبعاد",
    tourClose: "إغلاق الجولة",
    viewVenue: "عرض المكان",
    book: "احجز طاولة",
    website: "الموقع",
    menu: "القائمة",
    whatsapp: "واتساب",
    directions: "الاتجاهات",
    address: "العنوان",
    hours: "مواعيد العمل",
    hoursUnknown: "تأكّد من مواعيد العمل مع المكان قبل زيارتك.",
    closed: "مغلق",
    onTheMap: "على الخريطة",
    distinction: "التميّز",
    editorsNote: "كلمة المحرّرين",
    highlights: "أبرز ما فيه",
    whatToOrder: "ماذا تطلب",
    goodToKnow: "معلومات مفيدة",
    awardsPress: "جوائز وصحافة",
    cuisine: "المطبخ",
    price: "الأسعار",
    priceBands: ["أقل من 500 جنيه للشخص", "من 500 إلى 1,500 جنيه للشخص", "من 1,500 إلى 3,500 جنيه للشخص", "أكثر من 3,500 جنيه للشخص"],
    call: "اتصل",
    instagram: "إنستغرام",
    facebook: "فيسبوك",
    fromTheScan: "من المسح ثلاثي الأبعاد",
    alsoIn: "أيضاً في",
    back: "المختارات",
    tourCaption: "تصوير VirtualSpace. اسحب للتلفّت، واضغط على الأرض للتجوّل. يعمل على الهواتف والنظارات.",
    language: "English",
    languageLabel: "Read in English",
    footer: "اختارها محرّرون يزورون كل مكان. من VirtualSpace التي صوّرت كل غرفة بتقنية ثلاثية الأبعاد.",
    footerTwin: "Know Egypt like a local.",
    days: { mon: "الإثنين", tue: "الثلاثاء", wed: "الأربعاء", thu: "الخميس", fri: "الجمعة", sat: "السبت", sun: "الأحد" },
    groups: { see: "شاهد", do: "جرّب", eat: "كُل واشرب ونَم" },
    browseEyebrow: "حسب الفئة",
    browseTitle: "ماذا تبحث عن؟",
    browseTwin: "What are you after?",
    search: {
      label: "بحث",
      placeholder: "ابحث عن أماكن وفعاليات وأخبار",
      title: "بحث",
      twin: "Search",
      resultsFor: "نتائج البحث عن «{q}»",
      none: "لا نتائج لـ «{q}».",
      hint: "جرّب كلمة أخرى، أو اسم حي، أو نوع المكان.",
      venues: "الأماكن",
      events: "الفعاليات",
      stories: "الأخبار",
      go: "ابحث",
      suggestions: "اقتراحات",
    },
    saved: {
      save: "احفظ",
      saved: "محفوظ",
      unsave: "إزالة من المحفوظات",
      title: "أماكني المحفوظة",
      twin: "Saved places",
      link: "المحفوظات",
      empty: "لا شيء محفوظ بعد. اضغط «احفظ» على أي مكان ليبقى هنا.",
      note: "محفوظ على هذا الجهاز فقط. شارك قائمة لتحتفظ بها في أي مكان.",
      countOne: "{n} مكان",
      countMany: "{n} أماكن",
      listName: "سمِّ هذه القائمة",
      listPlaceholder: "عطلة في القاهرة",
      share: "شارك هذه القائمة",
      copyLink: "نسخ الرابط",
      copied: "تم نسخ الرابط",
      sharedTitle: "قائمة مشاركة معك",
      sharedBy: "من Localized مصر",
      saveAll: "احفظ الكل في أماكني",
      clear: "امسح أماكني المحفوظة",
      confirmClear: "هل تريد إزالة كل الأماكن المحفوظة؟",
      missing: "بعض الأماكن في هذه القائمة لم تعد ضمن المختارات.",
      map: "على الخريطة",
    },
    newsletter: {
      title: "الفعاليات، كل خميس",
      twin: "Every Thursday",
      body: "رسالة واحدة في الأسبوع: ما يحدث في مصر، الأماكن الجديدة في المختارات، والأخبار التي تستحق وقتك. مجاناً، بالعربية أو الإنجليزية.",
      email: "بريدك الإلكتروني",
      regions: "المناطق التي تهمّك",
      allRegions: "كل مصر",
      submit: "اشترك",
      submitting: "جارٍ الإرسال…",
      pending: "افتح بريدك وأكّد الاشتراك، وستكون معنا.",
      confirmed: "أنت معنا. نراك الخميس.",
      confirmedBody: "تصلك أول رسالة صباح الخميس المقبل. يمكنك إلغاء الاشتراك من أي رسالة.",
      unsubscribed: "تم إلغاء اشتراكك.",
      unsubscribedBody: "لن تصلك رسائل أخرى من Localized مصر. يمكنك الاشتراك مجدداً في أي وقت.",
      invalid: "هذا الرابط غير صالح أو سبق استخدامه.",
      error: "لم ينجح ذلك. تحقّق من العنوان وحاول مجدداً.",
      tooMany: "محاولات كثيرة. انتظر قليلاً من فضلك.",
      whatsapp: "تابعنا على واتساب",
      whatsappBody: "مختارات الأسبوع في قناة واتساب.",
      rss: "خلاصة RSS",
      privacy: "نستخدم عنوانك لهذه الرسالة فقط.",
    },
    filters: {
      price: "الأسعار",
      distinction: "التميّز",
      tour: "جولة ثلاثية الأبعاد",
      sort: "الترتيب",
      sortDefault: "ترتيب المحرّرين",
      sortName: "أبجدياً",
      sortDistinction: "الأعلى تميّزاً أولاً",
      sortNearest: "الأقرب أولاً",
      clear: "امسح الفلاتر",
      showMap: "الخريطة",
      showList: "القائمة",
      any: "الكل",
      more: "فلاتر أكثر",
    },
    eventPage: {
      addToCalendar: "أضف إلى التقويم",
      google: "تقويم Google",
      ics: "Apple وOutlook (.ics)",
      when: "متى",
      where: "أين",
      nextDates: "المواعيد القادمة",
      weekly: "كل أسبوع",
      monthly: "كل شهر",
      until: "حتى {date}",
      tickets: "التذاكر والتفاصيل",
      past: "انتهت هذه الفعالية. إليك ما يحدث بعدها.",
      more: "المزيد من الفعاليات",
      back: "الفعاليات",
    },
    collection: {
      placesIn: "أماكن في {name}",
      category: "{name} في مصر",
      eventsIn: "الفعاليات في {name}",
      storiesIn: "أخبار من {name}",
      areas: "الأحياء",
      categories: "الفئات",
      regions: "المناطق",
      onTheMap: "على الخريطة",
      introOne: "{count} مكان زاره محرّرونا.",
      introMany: "{count} أماكن، زار محرّرونا كلاً منها.",
      empty: "لا شيء هنا بعد. المحرّرون يعملون على ذلك.",
      seeAll: "كل المختارات",
    },
    reviews: {
      bestFor: "الأنسب لـ",
      title: "ماذا يقول الزوّار",
      rating: "{rating} على Google · {count} تقييم",
    },
    atThisVenue: "ماذا يحدث هنا",
    breadcrumbHome: "Localized مصر",
    errors: {
      notFound: "ليس على الخريطة",
      notFoundBody: "لم نجد هذه الصفحة. ربما انتقلت، أو لم تكن موجودة.",
      error: "حدث خطأ ما",
      errorBody: "تعذّر تحميل الصفحة. حاول مجدداً بعد قليل.",
      retry: "حاول مجدداً",
      home: "العودة إلى Localized مصر",
      loading: "جارٍ التحميل…",
    },
  },
} as const;

export type GuidePublicCopy = (typeof GUIDE_PUBLIC_COPY)[GuideLang];
